import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SIZE_PRICING, DIFFICULTY, CLEANUP_UPSELLS } from "../lib/site";

const selectClass =
    "w-full bg-brand-elevated border border-brand-line rounded px-4 py-3 font-mono text-sm text-brand-cream focus:outline-none focus:border-brand-amber appearance-none cursor-pointer";

const Estimator = () => {
    const navigate = useNavigate();
    const [size, setSize] = useState('25"');
    const [situation, setSituation] = useState(DIFFICULTY[0].label);
    const [cleanups, setCleanups] = useState([]);

    const range = useMemo(() => {
        const base = SIZE_PRICING.find((s) => s.size === size)?.price ?? 0;
        const sit = DIFFICULTY.find((d) => d.label === situation);
        const cleanupSel = CLEANUP_UPSELLS.filter((c) => cleanups.includes(c.label));
        const min = base + (sit?.min ?? 0) + cleanupSel.reduce((a, c) => a + c.min, 0);
        const max = base + (sit?.max ?? 0) + cleanupSel.reduce((a, c) => a + c.max, 0);
        return [min, max];
    }, [size, situation, cleanups]);

    const toggleCleanup = (label) =>
        setCleanups((prev) =>
            prev.includes(label) ? prev.filter((c) => c !== label) : [...prev, label]
        );

    return (
        <Reveal>
            <div
                id="estimator"
                className="rounded-lg border border-brand-line bg-brand-surface overflow-hidden"
                data-testid="estimator-panel"
            >
                <div className="px-6 sm:px-8 py-5 border-b border-brand-line bg-brand-elevated/70 flex items-center justify-between">
                    <Eyebrow>Instant estimate</Eyebrow>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-brand-moss hidden sm:block">
                        Built from the price list below
                    </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="p-6 sm:p-8 space-y-6">
                        <div>
                            <label
                                htmlFor="estimator-size"
                                className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2"
                            >
                                1 · Stump size (diameter at ground level)
                            </label>
                            <select
                                id="estimator-size"
                                data-testid="estimator-diameter-slider"
                                value={size}
                                onChange={(e) => setSize(e.target.value)}
                                className={selectClass}
                            >
                                {SIZE_PRICING.map((s) => (
                                    <option key={s.size} value={s.size}>
                                        {s.size} — starting ${s.price}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                htmlFor="estimator-situation"
                                className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2"
                            >
                                2 · Situation
                            </label>
                            <select
                                id="estimator-situation"
                                data-testid="estimator-situation-select"
                                value={situation}
                                onChange={(e) => setSituation(e.target.value)}
                                className={selectClass}
                            >
                                {DIFFICULTY.map((d) => (
                                    <option key={d.label} value={d.label}>
                                        {d.label} — {d.add}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                3 · Cleanup add-ons (optional, pick any)
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {CLEANUP_UPSELLS.map((c) => {
                                    const active = cleanups.includes(c.label);
                                    return (
                                        <button
                                            key={c.label}
                                            type="button"
                                            data-testid={`estimator-cleanup-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            onClick={() => toggleCleanup(c.label)}
                                            className={`font-mono text-xs px-3.5 py-2 rounded-full border transition-colors ${
                                                active
                                                    ? "bg-brand-amber text-brand-ink border-brand-amber font-bold"
                                                    : "border-brand-line text-brand-sage hover:border-brand-amber hover:text-brand-cream"
                                            }`}
                                        >
                                            {c.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="border-t lg:border-t-0 lg:border-l border-brand-line bg-brand-elevated/40 p-6 sm:p-8 flex flex-col">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-3">
                            Estimated range
                        </p>
                        <p
                            data-testid="estimator-total-display"
                            className="font-mono font-bold text-brand-amber text-4xl sm:text-5xl tracking-tight mb-3"
                        >
                            {range[0] === range[1]
                                ? `$${range[0].toLocaleString()}`
                                : `$${range[0].toLocaleString()} – $${range[1].toLocaleString()}`}
                        </p>
                        <p className="text-brand-sage text-sm leading-relaxed mb-6">
                            Estimate only — combine it with the surcharges that
                            apply on site. Your exact price is confirmed when we
                            see the stump.
                        </p>
                        <button
                            type="button"
                            data-testid="estimator-use-in-quote-btn"
                            onClick={() =>
                                navigate("/quote", {
                                    state: { size, situation, cleanups },
                                })
                            }
                            className="mt-auto inline-flex items-center justify-center gap-2 bg-brand-amber text-brand-ink font-bold px-6 py-3.5 rounded-full hover:bg-brand-amberHover transition-colors"
                        >
                            Use this in my free quote
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </Reveal>
    );
};

export default Estimator;
